import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plane, Calendar, Target, MapPin, Thermometer, CloudRain } from 'lucide-react';
import { TripPreferences } from '../types/weather';
import { tripPlanningApi, TripDestination, BestTimeResult } from '../services/tripPlanningApi';
import { LocationTypeahead } from './LocationTypeahead';
import { LocationSuggestion, locationSearchApi } from '../services/locationSearchApi';

interface PlanningTripFlowProps {
  onBack: () => void;
  onLoading: (loading: boolean) => void;
  onError: (error: string | null) => void;
}

export const PlanningTripFlow: React.FC<PlanningTripFlowProps> = ({ onBack, onLoading, onError }) => {
  const [step, setStep] = useState<'type' | 'known-dates' | 'unknown-dates' | 'results'>('type');
  const [_tripType, setTripType] = useState<TripPreferences['tripType'] | null>(null);
  const [results, setResults] = useState<TripDestination[] | BestTimeResult | null>(null);

  // Scroll to top whenever step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const handleTripTypeSelect = (type: TripPreferences['tripType']) => {
    setTripType(type);
    if (type === 'known-timeframe') {
      setStep('known-dates');
    } else {
      setStep('unknown-dates');
    }
  };

  const handleKnownDatesSubmit = async (data: {
    startDate: string;
    endDate: string;
    preferences: {
      temperature: 'hot' | 'warm' | 'cool' | 'cold';
      precipitation: 'dry' | 'occasional-rain' | 'regular-rain';
      goal: 'avoid-rain' | 'warm-weather' | 'cool-weather' | 'mild-weather';
    };
  }) => {
    onLoading(true);
    onError(null);

    try {
      const response = await tripPlanningApi.findDestinationsForDates(
        data.startDate,
        data.endDate,
        data.preferences
      );
      setResults(response.data);
      setStep('results');
    } catch (error) {
      console.error('Trip planning error:', error);
      onError(error instanceof Error ? error.message : 'Failed to find destinations');
    } finally {
      onLoading(false);
    }
  };

  const handleUnknownDatesSubmit = async (location: string) => {
    onLoading(true);
    onError(null);

    try {
      const response = await tripPlanningApi.findBestTimeToVisit(location);
      setResults(response.data);
      setStep('results');
    } catch (error) {
      console.error('Best time error:', error);
      onError(error instanceof Error ? error.message : 'Failed to find best time to visit');
    } finally {
      onLoading(false);
    }
  };

  if (step === 'results' && results) {
    return (
      <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Plane className="h-6 w-6 text-green-600 dark:text-green-400" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Trip Results</h2>
        </div>
        <button
          onClick={() => setStep('type')}
          className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>
      </div>

        {Array.isArray(results) ? (
          <TripDestinationsResults destinations={results} />
        ) : (
          <BestTimeResults bestTime={results} />
        )}
      </div>
    );
  }

  if (step === 'known-dates') {
    return (
      <KnownDatesStep
        onBack={() => setStep('type')}
        onSubmit={handleKnownDatesSubmit}
      />
    );
  }

  if (step === 'unknown-dates') {
    return (
      <UnknownDatesStep
        onBack={() => setStep('type')}
        onSubmit={handleUnknownDatesSubmit}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Plane className="h-6 w-6 text-green-600 dark:text-green-400" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Planning a Trip</h2>
        </div>
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button
          onClick={() => handleTripTypeSelect('known-timeframe')}
          className="card hover:shadow-lg transition-shadow duration-200 text-left group"
        >
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center mb-6 group-hover:shadow-lg transition-shadow duration-200">
            <Calendar className="h-8 w-8 text-white" />
          </div>
          
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
            I have specific travel dates
          </h3>
          
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Find the best destinations with great weather for your planned travel dates.
          </p>
          
          <ul className="space-y-2 text-sm text-gray-500 dark:text-gray-400">
            <li>• Find destinations perfect for your dates</li>
            <li>• Avoid rain/snow or extreme temperatures</li>
            <li>• Get weather-based recommendations</li>
          </ul>
        </button>

        <button
          onClick={() => handleTripTypeSelect('unknown-timeframe')}
          className="card hover:shadow-lg transition-shadow duration-200 text-left group"
        >
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-purple-500 to-purple-600 flex items-center justify-center mb-6 group-hover:shadow-lg transition-shadow duration-200">
            <Target className="h-8 w-8 text-white" />
          </div>
          
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
            When is the best time to visit?
          </h3>
          
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Discover the ideal months to visit your dream destination based on weather patterns.
          </p>
          
          <ul className="space-y-2 text-sm text-gray-500 dark:text-gray-400">
            <li>• Find the best months for any location</li>
            <li>• Avoid bad weather seasons</li>
            <li>• Plan around climate patterns</li>
          </ul>
        </button>
      </div>
    </div>
  );
};

const KnownDatesStep: React.FC<{
  onBack: () => void;
  onSubmit: (data: any) => void;
}> = ({ onBack, onSubmit }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [temperature, setTemperature] = useState<'hot' | 'warm' | 'cool' | 'cold'>('warm');
  const [precipitation, setPrecipitation] = useState<'dry' | 'occasional-rain' | 'regular-rain'>('dry');
  const [goal, setGoal] = useState<'avoid-rain' | 'warm-weather' | 'cool-weather' | 'mild-weather'>('warm-weather');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate) return;

    onSubmit({
      startDate,
      endDate,
      preferences: {
        temperature,
        precipitation,
        goal
      }
    });
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Calendar className="h-6 w-6 text-green-600 dark:text-green-400" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Trip with Known Dates</h2>
        </div>
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Travel Dates</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-800 dark:text-white"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-800 dark:text-white"
                required
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Weather Preferences</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Temperature Preference
              </label>
              <select
                value={temperature}
                onChange={(e) => setTemperature(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-800 dark:text-white"
              >
                <option value="hot">Hot (80°F+)</option>
                <option value="warm">Warm (65-80°F)</option>
                <option value="cool">Cool (45-65°F)</option>
                <option value="cold">Cold (below 45°F)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Precipitation Preference
              </label>
              <select
                value={precipitation}
                onChange={(e) => setPrecipitation(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-800 dark:text-white"
              >
                <option value="dry">Dry climate (minimal precipitation)</option>
                <option value="occasional-rain">Occasional precipitation (moderate)</option>
                <option value="regular-rain">Regular precipitation (frequent)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Primary Goal
              </label>
              <select
                value={goal}
                onChange={(e) => setGoal(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-800 dark:text-white"
              >
                <option value="avoid-rain">Avoid precipitation</option>
                <option value="warm-weather">Warm weather</option>
                <option value="cool-weather">Cool weather</option>
                <option value="mild-weather">Mild weather</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" className="btn-primary">
            Find Destinations
          </button>
        </div>
      </form>
    </div>
  );
};

const UnknownDatesStep: React.FC<{
  onBack: () => void;
  onSubmit: (location: string) => void;
}> = ({ onBack, onSubmit }) => {
  const [selectedLocation, setSelectedLocation] = useState<LocationSuggestion | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocation) return;
    onSubmit(selectedLocation.display_name);
  };

  const handleLocationAdd = (location: LocationSuggestion) => {
    setSelectedLocation(location);
  };

  const handleLocationRemove = () => {
    setSelectedLocation(null);
  };

  const popularDestinations = [
    'Paris', 'London', 'Tokyo', 'New York', 'Los Angeles',
    'Barcelona', 'Rome', 'Sydney', 'Bangkok', 'San Francisco'
  ];

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Target className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Best Time to Visit</h2>
        </div>
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Where do you want to go?</h3>
          
          <LocationTypeahead
            onLocationAdd={handleLocationAdd}
            placeholder="Search for destinations (e.g., Paris, France or Tokyo, Japan)"
            className="mb-4"
          />

          {selectedLocation && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Selected destination:</h4>
              <div className="flex items-center bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 px-3 py-2 rounded-lg">
                <span className="mr-2">{selectedLocation.type === 'city' ? '🏙️' : selectedLocation.type === 'town' ? '🏘️' : '📍'}</span>
                <span className="flex-1">{selectedLocation.display_name}</span>
                <button
                  onClick={handleLocationRemove}
                  className="ml-2 text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
              <MapPin className="h-4 w-4 mr-2" />
              Popular destinations:
            </h4>
            <div className="flex flex-wrap gap-2">
              {popularDestinations.map((dest) => (
                <button
                  key={dest}
                  type="button"
                  onClick={async () => {
                    try {
                      // Use the proper location search API instead of creating mock data
                      const locations = await locationSearchApi.searchLocations(dest);
                      if (locations.length > 0) {
                        handleLocationAdd(locations[0]); // Use the first/best match
                      }
                    } catch (error) {
                      console.error(`Error searching for ${dest}:`, error);
                    }
                  }}
                  className="flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  <MapPin className="h-3 w-3 mr-1" />
                  {dest}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button 
            type="submit" 
            disabled={!selectedLocation}
            className={`btn-primary ${!selectedLocation ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Find Best Time to Visit
          </button>
        </div>
      </form>
    </div>
  );
};

const TripDestinationsResults: React.FC<{ destinations: TripDestination[] }> = ({ destinations }) => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
      Best Destinations for Your Trip
    </h3>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {destinations.map((dest) => (
        <div key={dest.location} className="card">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-2">
              <MapPin className="h-5 w-5 text-green-600 dark:text-green-400" />
              <h4 className="font-semibold text-gray-900 dark:text-white">{dest.location}</h4>
            </div>
            <span className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-2 py-1 rounded-full text-sm font-medium">
              {dest.weatherScore}% match
            </span>
          </div>
          
          <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">{dest.summary}</p>
          
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div className="flex items-center space-x-1">
              <Thermometer className="h-4 w-4 text-red-500" />
              <span className="text-gray-600 dark:text-gray-300">
                {dest.details?.averageHigh || 'N/A'}°F
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <CloudRain className="h-4 w-4 text-blue-500" />
              <span className="text-gray-600 dark:text-gray-300">
                {dest.details?.rainChance || 'N/A'}% precipitation
              </span>
            </div>
            <div className="text-gray-600 dark:text-gray-300">
              {dest.details?.conditions || 'Unknown conditions'}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const BestTimeResults: React.FC<{ bestTime: BestTimeResult }> = ({ bestTime }) => (
  <div className="space-y-6">
    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
      Best Time to Visit {bestTime.location}
    </h3>
    
    <div className="card">
      <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Best Months</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {bestTime.bestMonths.map((month) => (
          <div key={month.month} className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h5 className="font-medium text-gray-900 dark:text-white">{month.month}</h5>
              <span className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-2 py-1 rounded-full text-xs">
                {month.weatherScore}% score
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">{month.summary}</p>
          </div>
        ))}
      </div>
    </div>
    
    <div className="card">
      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Climate Summary</h4>
      <p className="text-gray-600 dark:text-gray-300">{bestTime.yearRoundSummary}</p>
      
      {bestTime.worstMonths.length > 0 && (
        <div className="mt-4">
          <h5 className="font-medium text-gray-900 dark:text-white mb-2">Avoid these months:</h5>
          <div className="flex flex-wrap gap-2">
            {bestTime.worstMonths.map((month) => (
              <span key={month} className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 px-2 py-1 rounded-full text-sm">
                {month}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);